import { UserRole } from '../enums/userRole';
import { configApi } from './apiClient';
import { hasMinRole } from './permissions';
import { toast } from './toast';

function extractErrorMessage(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (Array.isArray(value)) {
        const parts = value
            .map((item) => extractErrorMessage(item))
            .filter(Boolean);
        return parts.join(', ');
    }

    if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const nestedValue =
            record.message ??
            record.responseData ??
            record.error ??
            record.details ??
            record.code;

        if (nestedValue !== undefined && nestedValue !== value) {
            const extracted = extractErrorMessage(nestedValue);
            if (extracted) {
                return extracted;
            }
        }

        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }

    if (value == null) {
        return '';
    }

    return String(value);
}

function cleanErrorMessage(message: unknown, maxLength = 200): string {
    // Normalize structured payloads before trimming and removing any HTML noise.
    const text = extractErrorMessage(message).replace(/<[^>]*>/g, '').trim();
    if (text.length > maxLength) {
        return text.slice(0, maxLength) + '...';
    }
    return text;
}

function getConnectionTestError(response: any): string {
    const primary = response?.message || response?.responseData || response?.error;
    if (primary) {
        return cleanErrorMessage(primary);
    }
    const code = response?.data?.code;
    if (code) {
        return cleanErrorMessage(code);
    }
    return cleanErrorMessage('Unknown error');
}

export async function testConnectionApi(
    configId: string | number | undefined,
    setTesting: (b: boolean) => void,
    connectionData: any,
    user: { role?: { id: number } } | null
) {
    if (!configId) {
        toast.error('Please save configuration first');
        return;
    }
    setTesting(true);
    try {
        if (hasMinRole(user?.role?.id, UserRole.Admin)) {
            const response = await configApi.testConnection(configId, connectionData);
            if (response.status !== 200) {
                toast.error(`Connection test completed with errors: ${getConnectionTestError(response)}`);
            } else {
                toast.success('Connection test completed successfully');
            }
        } else {
            const response = await configApi.testBasicConnection(configId);
            if (response.status !== 200) {
                toast.error(`Connection test completed with errors: ${getConnectionTestError(response)}`);
            } else {
                toast.success('Connection test completed successfully');
            }
        }
    } catch (error: any) {
        toast.error(`Connection test failed: ${cleanErrorMessage(error?.message || error || 'Unknown error')}`);
    } finally {
        setTesting(false);
    }
}
